import { useEffect, useRef } from "react";

export function useEnsureOption({
  filterKey,
  addedFilters,
  options,
  setOptions,
  fetchByValue,
  setSelected, // ← new (optional) setter from useState
  isMulti = false,
}) {
  // remember which values we've already fetched to avoid repeat calls
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    const run = async () => {
      const filter = addedFilters?.[filterKey];
      if (!filter) return;

      const raw = filter.value;
      if (!raw) return;

      const values = isMulti && Array.isArray(raw) ? raw : [raw];

      // find values missing from options and not fetched yet
      const toFetch = values.filter(
        (v) =>
          !options.some((opt) => opt.value === v) && !fetchedRef.current.has(v)
      );
      if (toFetch.length === 0) return; // nothing to do

      try {
        const fetched = await Promise.all(toFetch.map(fetchByValue)); // [{label,value}, ...]

        // mark fetched so we never refetch the same values
        // mark even failed (null/undefined) to avoid infinite loop
        values.forEach((v, i) => {
          const result = Array.isArray(fetched[i]) ? fetched[i][0] : fetched[i];
          if (result?.value) {
            fetchedRef.current.add(result.value);
          } else {
            fetchedRef.current.add(v); // mark the original raw value
          }
        });

        // append new options, avoiding duplicates
        setOptions((prev) => {
          const next = [...prev];
          fetched
            .flat() // in case fetchByValue returns [[opt]]
            .filter(Boolean) // remove null/undefined entries
            .forEach((opt) => {
              if (!next.some((o) => o.value === opt.value)) {
                next.push(opt);
              }
            });
          return next;
        });

        /* -----------------------------------------------------------
           If a setSelected setter is provided, update it immediately
           so the dropdown UI reflects the newly fetched option(s).
        ----------------------------------------------------------- */
        if (setSelected) {
          if (isMulti) {
            const flatFetched = fetched.flat().filter(Boolean);
            const foundMulti = values
              .map(
                (v) =>
                  flatFetched.find((opt) => opt?.value === v) ||
                  options.find((opt) => opt.value === v)
              )
              .filter(Boolean);
            setSelected(foundMulti);
          } else {
            const flatFetched = fetched.flat().filter(Boolean);
            console.log("fethed: ", fetched);
            const foundSingle =
              flatFetched.find((opt) => opt?.value === raw) ||
              options.find((opt) => opt.value === raw);
            if (foundSingle) setSelected(foundSingle);
          }
        }
      } catch (err) {
        console.error(`Failed to load option(s) for “${filterKey}”`, err);
      }
    };

    run();
  }, [
    filterKey,
    addedFilters,
    options,
    setOptions,
    fetchByValue,
    setSelected, // include in deps
    isMulti,
  ]);
}
